import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import {
  Edge,
  MiniMap,
  ReactFlow,
  ReactFlowInstance,
  ReactFlowProvider,
  useEdgesState,
  useKeyPress,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { Theme } from '../../../../theme';
import { withChildren } from '../../../../utils/react';
import { EmployeeInfoSidebar } from '../../components/EmployeeInfoSidebar';
import { DetailsFilter } from './Actions/DetailsFilter';
import { OrgChartIconButton } from './Actions/OrgChartIconButton';
import EmployeeNode from './EmployeeNode';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  getChildrenIds,
  getNodesAndEdges,
} from './helper';
import './index.css';
import OrgChartFilters from './OrgChartFilters';
import { Search } from './Search';
import { EmployeeDetail, OrgChartEmployee, OrgChartNode } from './types';
import { useOrgChartFilters } from './useOrgChartFilters';

type Props = {
  employees: OrgChartEmployee[];
};

const nodeTypes = {
  employeeNode: EmployeeNode,
};

const FullScreenWithChildren = withChildren(FullScreen);

export const Content: React.FC<React.PropsWithChildren<Props>> = ({
  employees,
}) => {
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance<OrgChartNode, Edge>>();
  const { setCenter } = useReactFlow();
  const fullScreenHandle = useFullScreenHandle();
  const [selectedEmployee, setSelectedEmployee] =
    useState<OrgChartEmployee | null>(null);

  const {
    filters,
    setFilters,
    filteredEmployees,
    hasActiveFilters,
    clearFilters,
  } = useOrgChartFilters(employees);

  const [modifiedEmployees, setModifiedEmployees] = useState<
    OrgChartEmployee[]
  >([]);
  const [searchedId, setSearchedId] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState<string>('focus');
  const [nodes, setNodes, onNodesChange] = useNodesState<OrgChartNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [detailsSettings] = useState<EmployeeDetail[]>([
    'name',
    'avatar',
    'position',
    'department',
  ]);

  //Equal is used to handle some keyboards that have a key with two flavor (=, +)
  //Same for Minus
  const plusKeyboardPressed = useKeyPress(['Equal', 'NumpadAdd']);
  const subtractKeyboardPressed = useKeyPress(['Minus', 'NumpadSubtract']);

  const [selectedSettings, setSelectedSettings] =
    useState<string[]>(detailsSettings);

  useEffect(() => {
    setModifiedEmployees(filteredEmployees);
  }, [filteredEmployees]);

  useEffect(() => {
    const { nodes, edges } = getNodesAndEdges(
      modifiedEmployees,
      selectedSettings,
    );
    setNodes(nodes);
    setEdges(edges);
  }, [
    modifiedEmployees,
    setEdges,
    setNodes,
    selectedSettings,
    reactFlowInstance,
  ]);

  // Refit the viewport when the filtered set changes so the compressed
  // layout is visible without manual scrolling. Skip while a search is
  // active — the search effect manages its own centering.
  useEffect(() => {
    if (!reactFlowInstance) return;
    if (searchedId !== null) return;

    const timer = setTimeout(() => {
      reactFlowInstance.fitView({ duration: 400, padding: 0.2 });
    }, 50);
    return () => clearTimeout(timer);
  }, [filteredEmployees, reactFlowInstance, searchedId]);

  useEffect(() => {
    if (searchedId !== null && searchMode === 'focus') {
      const isVisible = filteredEmployees.some((e) => e.id === searchedId);
      if (!isVisible) {
        clearFilters();
        return;
      }
      const searchedNode = nodes.find((n) => n.data.employee.id === searchedId);
      if (searchedNode != null) {
        // v12 measures nodes asynchronously; fall back to the layout size until
        // the first measurement lands, otherwise the centre is NaN.
        const width = searchedNode.measured?.width ?? NODE_WIDTH;
        const height = searchedNode.measured?.height ?? NODE_HEIGHT;
        const x = searchedNode.position.x + width / 2;
        const y = searchedNode.position.y + height / 2;
        const zoom = 3;
        setCenter(x, y, { zoom, duration: 1000 });
      }
    }
  }, [
    nodes,
    searchMode,
    searchedId,
    setCenter,
    filteredEmployees,
    clearFilters,
  ]);

  useEffect(() => {
    if (searchedId !== null && searchMode === 'find') {
      let employee = filteredEmployees.find((e) => e.id === searchedId);
      if (!employee) {
        // Employee is hidden by active filters — clear filters so they become visible
        clearFilters();
        return;
      }
      employee.isCollapsed = true;
      setModifiedEmployees([employee]);
    } else {
      setModifiedEmployees(filteredEmployees);
    }
  }, [filteredEmployees, searchedId, searchMode, clearFilters]);

  useEffect(() => {
    reactFlowInstance?.zoomIn();
  }, [reactFlowInstance, plusKeyboardPressed]);

  useEffect(() => {
    reactFlowInstance?.zoomOut();
  }, [reactFlowInstance, subtractKeyboardPressed]);

  const collapse = (node: OrgChartNode) => {
    const childrenIds = getChildrenIds(node, nodes, edges, []);
    setModifiedEmployees((employees) => {
      const filteredEmployees = employees.filter(
        (e) => !childrenIds.includes(e.id),
      );
      filteredEmployees.find((e) => e.id === node.id)!.isCollapsed = true;
      return filteredEmployees;
    });
  };

  const expand = (node: OrgChartNode) => {
    const children = filteredEmployees.filter((e) => e.manager?.id === node.id);
    children.map((c) => (c.isCollapsed = true));
    setModifiedEmployees((employees) => {
      employees.find((e) => e.id === node.id)!.isCollapsed = false;
      return employees.concat(children);
    });
  };

  const isClickOnExpandCollapse = (_event: React.MouseEvent) => {
    // @ts-ignore
    const { dataset, tagName } = _event.target;
    return (
      dataset.id === 'no-of-employees-container' ||
      tagName === 'path' ||
      tagName === 'svg'
    );
  };

  const onNodeClick = (_event: React.MouseEvent, node: OrgChartNode) => {
    if (isClickOnExpandCollapse(_event)) {
      if (node.data.employee.isCollapsed) {
        expand(node);
      } else {
        collapse(node);
      }
    } else {
      setSelectedEmployee(node.data.employee);
    }
  };

  const onCloseSidebar = () => setSelectedEmployee(null);

  return (
    <FullScreenWithChildren handle={fullScreenHandle}>
      <Card>
        <Card.Header>
          <div
            className={classNames(
              'd-flex',
              'justify-content-between',
              'align-items-center',
              'small',
            )}>
            <Search
              employees={employees}
              searchedId={searchedId}
              setSearchedId={setSearchedId}
              searchMode={searchMode}
              setSearchMode={setSearchMode}
            />
            <OrgChartFilters
              employees={employees}
              filters={filters}
              onFiltersChange={setFilters}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
            />
            <div
              className={classNames('d-flex', 'align-items-center', 'gap-4')}>
              <DetailsFilter
                detailsSettings={detailsSettings}
                selectedIds={selectedSettings}
                onSelectedIdsChange={setSelectedSettings}
              />
              <div
                className={classNames('d-flex', 'align-items-center', 'gap-3')}>
                {fullScreenHandle.active ? (
                  <OrgChartIconButton
                    icon="compress-outline"
                    onClick={() => fullScreenHandle.exit()}
                  />
                ) : (
                  <OrgChartIconButton
                    icon="expand-outline"
                    onClick={() => fullScreenHandle.enter()}
                  />
                )}
                <OrgChartIconButton
                  icon="remove-outline"
                  onClick={() => reactFlowInstance?.zoomOut()}
                />
                <OrgChartIconButton
                  icon="add-outline"
                  onClick={() => reactFlowInstance?.zoomIn()}
                />
              </div>
            </div>
          </div>
        </Card.Header>
        <Card.Body
          className="p-0"
          style={{ height: fullScreenHandle.active ? '100vh' : '75vh' }}>
          <ReactFlow
            style={{ cursor: 'grab' }}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            nodesDraggable={false}
            onInit={setReactFlowInstance}
            fitView>
            <MiniMap nodeColor={Theme.color.blue3} nodeStrokeWidth={1} />
          </ReactFlow>
        </Card.Body>

        <EmployeeInfoSidebar
          show={!!selectedEmployee}
          onClose={onCloseSidebar}
          employee={selectedEmployee}
        />
      </Card>
    </FullScreenWithChildren>
  );
};

export const OrgChart: React.FC<React.PropsWithChildren<Props>> = (props) => (
  <ReactFlowProvider>
    <Content {...props} />
  </ReactFlowProvider>
);
